import { Order } from "../../../types";
import { formatCurrencyBDT } from "../../../utils/currency";

export const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** A line's contribution to the total — gifts are always free. */
export const lineTotal = (line: { quantity: number; unit_price: number; is_gift?: boolean }) =>
    line.is_gift ? 0 : round2(Number(line.unit_price) * line.quantity);

/**
 * The total the server will store after an item or charge change: items +
 * shipping − discount (OrderItemService._recalculate_order_total).
 */
export const orderTotalFor = (itemsTotal: number, order: Pick<Order, "shipping_amount" | "discount_amount">, shipping?: number) =>
    round2(itemsTotal + Number(shipping ?? order.shipping_amount ?? 0) - Number(order.discount_amount ?? 0));

export const itemsTotalOf = (order: Pick<Order, "items">) =>
    round2((order.items ?? []).reduce((sum, item) => sum + lineTotal(item), 0));

export interface MoneyImpact {
    oldTotal: number;
    newTotal: number;
    /** Net paid so far (refunds already subtracted). */
    paid: number;
}

/** "৳150 due", "৳40 overpaid", or "Fully paid" for a total against what's been paid. */
export function balanceText(total: number, paid: number): string {
    const diff = round2(total - paid);
    if (diff > 0) return `${formatCurrencyBDT(diff)} due`;
    if (diff < 0) return `${formatCurrencyBDT(-diff)} overpaid`;
    return "Fully paid";
}

/**
 * A courier consignment is created with a fixed amount to collect, and the
 * courier API can't change it afterwards — so say so whenever the total moves.
 */
export function codWarning(order: Pick<Order, "consignment_id" | "total_amount">, newTotal: number): string | null {
    if (!order.consignment_id || round2(newTotal) === round2(Number(order.total_amount))) return null;
    return (
        `A courier consignment (#${order.consignment_id}) already exists. The courier will still collect ` +
        `the amount it was created with — update it with the courier if needed. New total: ${formatCurrencyBDT(newTotal)}.`
    );
}
